import React, { useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  MdOutlineMailOutline,
  MdOutlinePhone,
  MdOutlineLocationOn,
} from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper";
import { RedirectCell } from "components/common/GridComponents";
import ClientComboBox from "components/common/ClientComboBox";
import AddClients from "pages/Clients/AddViewClients";

const CLIENT_TYPE_LABELS = { 0: "Person", 1: "Company" };
const formatClientType = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "number") return CLIENT_TYPE_LABELS[value] ?? "";
  if (typeof value === "string" && /^\d+$/.test(value))
    return CLIENT_TYPE_LABELS[Number(value)] ?? value;
  return value;
};

const IconBadge = ({ children }) => (
  <span
    className="d-inline-flex align-items-center justify-content-center rounded-circle bgBtnSecondary"
    style={{ width: 36, height: 36, color: "#000000", flexShrink: 0 }}
  >
    {children}
  </span>
);

const LeadClientInfoTile = ({ leadClient }) => (
  <Tile
    heading="Lead Client"
    height={"160px"}
    className="px-4 pt-2 pb-3 overflow-auto"
  >
    <div className="row pt-2 g-3">
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge>
          <MdOutlineMailOutline size={18} />
        </IconBadge>
        <span className="text-truncate fw-semibold">{leadClient?.email || "—"}</span>
      </div>
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge>
          <MdOutlinePhone size={18} />
        </IconBadge>
        <span className="text-truncate fw-semibold">{leadClient?.phone || "—"}</span>
      </div>
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge>
          <MdOutlineLocationOn size={18} />
        </IconBadge>
        <span className="text-truncate fw-semibold">{leadClient?.address || "—"}</span>
      </div>
    </div>
  </Tile>
);

const AddClientTile = ({ onAdd, allExistingIds = [] }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [pickerKey, setPickerKey] = useState(0);

  const handleAdd = () => {
    if (!selectedClient) return;
    const cid =
      selectedClient.CustomerID ||
      selectedClient.clientId ||
      selectedClient.clientID;
    if (!cid) return;
    if (allExistingIds.includes(cid)) {
      setSelectedClient(null);
      setPickerKey((k) => k + 1);
      return;
    }
    onAdd(selectedClient);
    setSelectedClient(null);
    setPickerKey((k) => k + 1);
  };

  return (
    <Tile
      heading="Additional Clients"
      height={"160px"}
      className="px-4 pt-2 pb-3 overflow-auto"
    >
      <div className="d-flex align-items-end justify-content-between gap-3 pt-2">
        <div style={{ minWidth: 280 }}>
          <Label className="d-block mb-1">Client</Label>
          <ClientComboBox
            key={pickerKey}
            val={selectedClient}
            width={"326px"}
            height="32px"
            onChangeClient={(c) => setSelectedClient(c)}
            excludeIds={allExistingIds}
            required={false}
          />
        </div>
        <Button
          className="common-btn"
          onClick={handleAdd}
          disabled={!selectedClient}
        >
          Add Client
        </Button>
      </div>
    </Tile>
  );
};

const ClientsGridTile = ({
  heading,
  rows,
  isView,
  onRemove,
  onView,
}) => {
  const ActionCell = (cellProps) => {
    if (isView) {
      return (
        <RedirectCell
          title="View Client"
          handleClick={() => onView?.(cellProps.dataItem)}
        />
      );
    }
    const item = cellProps.dataItem;
    return (
      <td className="text-center">
        <span
          role="button"
          onClick={() => onRemove?.(item)}
          title="Remove"
        >
          <RiDeleteBin6Line size={18} color="#d32f2f" />
        </span>
      </td>
    );
  };

  return (
    <Tile
      heading={heading}
      className="px-4 pt-2 pb-3 d-flex flex-column h-100"
      minHeight={300}
    >
      <div className="mt-2 flex-grow-1 d-flex flex-column overflow-auto">
        <Grid data={rows || []} style={{ height: "100%" }}>
          <GridColumn field="clientRef" title="Client Ref" width="100px" />
          <GridColumn field="clientName" title="Client Name" width="200px" />
          <GridColumn field="feeEarnerRef" title="Fee Earner" width="100px" />
          <GridColumn
            field="type"
            title="Type"
            width="80px"
            cell={(cellProps) => (
              <td>{formatClientType(cellProps.dataItem?.type)}</td>
            )}
          />
          <GridColumn field="phoneNo" title="Phone" width="110px" />
          <GridColumn field="email" title="Email" />
          <GridColumn field="address" title="Address" />
          <GridColumn
            field="noOfMatters"
            title="No of Matters"
            width="110px"
          />
          <GridColumn title=" " width="50px" cell={ActionCell} />
        </Grid>
      </div>
    </Tile>
  );
};

const AdditionalClientsTab = ({
  formType,
  leadClient,
  additionalClients = [],
  onChange,
}) => {
  const isView = formType === "VIEW";
  const [viewClient, setViewClient] = useState(null);

  const handleAdd = (client) => {
    const clientId =
      client.CustomerID || client.clientId || client.clientID;
    if (!clientId) return;
    const newRow = {
      clientId,
      clientRef: client.clientRef ?? client.CustomerRef ?? "",
      clientName: client.clientName ?? client.ContactName ?? "",
      feeEarnerRef: client.feeEarnerRef ?? "",
      type: client.type ?? "",
      phoneNo: client.phoneNo ?? "",
      email: client.email ?? "",
      address: client.address ?? "",
      noOfMatters: client.noOfMatters,
      order: additionalClients.length,
      active: true,
    };
    onChange?.([...(additionalClients || []), newRow]);
  };

  const handleRemove = (row) => {
    onChange?.(
      (additionalClients || []).filter(
        (r) => r.clientId !== row.clientId,
      ),
    );
  };

  const existingIds = [
    leadClient?.clientId,
    ...(additionalClients || []).map((r) => r.clientId),
  ].filter(Boolean);

  return (
    <TileWrapper className="h-100 d-flex flex-column">
      <div className="row g-2 flex-grow-1" style={{ minHeight: 0 }}>
        <div className="col-12 flex-shrink-0">
          {isView ? (
            <LeadClientInfoTile leadClient={leadClient} />
          ) : (
            <AddClientTile onAdd={handleAdd} allExistingIds={existingIds} />
          )}
        </div>
        <div className="col-12 d-flex" style={{ minHeight: 0, flex: "1 1 auto" }}>
          <div className="w-100 d-flex flex-column">
            <ClientsGridTile
              heading={isView ? "Details" : "Client Details"}
              rows={additionalClients}
              isView={isView}
              onRemove={handleRemove}
              onView={(row) => setViewClient(row)}
            />
          </div>
        </div>
      </div>
      {viewClient && (
        <Dialog
          className="dialog-xxl add-dialog"
          title="View Client"
          onClose={() => setViewClient(null)}
        >
          <AddClients
            clientId={viewClient.clientId}
            formId="view"
            title="View Client"
            type="VIEW"
            reference={viewClient.clientRef}
            onClose={() => setViewClient(null)}
          />
          <DialogActionsBar>
            <div className="dialog-footer">
              <Button
                className="common-btn bgBtnSecondary border-0 me-3"
                type="button"
                onClick={() => setViewClient(null)}
              >
                Close
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </TileWrapper>
  );
};

export default AdditionalClientsTab;
